// src/Home.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import Calendar from "../../components/Calendar/Calendar";
import Timeline from "../../components/Timeline/Timeline";
import Modal from "../../components/Modal/Modal";
import dayjs from "dayjs";
import axios from "axios";
import reservationsWS from "../../services/wsReservations";
import { useTranslation } from 'react-i18next';
import "./Home.css"; // We'll create this file for styles

// const API_BASE_URL = process.env.REACT_APP_BASE_DOMAIN;
const API_BASE_URL = "";

const Home = ({ currentUser, currentResourceGroup }) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reservationData, setReservationData] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [reservationToEdit, setReservationToEdit] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const unsubRef = useRef(null);
  const selectedDateRef = useRef(selectedDate);

  useEffect(() => {
    selectedDateRef.current = selectedDate;
  }, [selectedDate]);

  const formatReservations = (list) =>
    list.map((reservation) => ({
      id: reservation.id,
      date: reservation.occurrence_start
        ? dayjs(reservation.occurrence_start).format("YYYY-MM-DD")
        : dayjs(reservation.start).format("YYYY-MM-DD"),
      startTime: reservation.occurrence_start
        ? dayjs(reservation.occurrence_start).format("HH:mm")
        : dayjs(reservation.start).format("HH:mm"),
      endTime: reservation.occurrence_end
        ? dayjs(reservation.occurrence_end).format("HH:mm")
        : dayjs(reservation.end).format("HH:mm"),
      room: `Room ${String.fromCharCode(64 + reservation.resource_id)}`,
      roomId: reservation.resource_id,
      title: reservation.title || reservation.description || "Reserved",
      userName: reservation.user_name,
      description: reservation.description,
      can_delete: reservation.can_delete,
      can_edit: reservation.can_edit,
      recurring: reservation.recurring,
    }));

  const fetchRooms = useCallback(async () => {
    try {
      // Build URL with resource group filter
      let url = `${API_BASE_URL}/api/resources/`;
      if (currentResourceGroup) {
        url += `?group_id=${currentResourceGroup.id}`;
      }

      const response = await axios.get(url);
      setRooms(response.data);
    } catch (error) {
      // ignore; will retry later
    }
  }, [currentResourceGroup]); // Re-create when resource group changes

  const fetchReservationsHttp = useCallback(async (date) => {
    try {
      // Build URL with resource group filter
      let url = `${API_BASE_URL}/api/reservations/${date}-00-00-00`;
      if (currentResourceGroup) {
        url += `?group_id=${currentResourceGroup.id}`;
      }

      const response = await axios.get(url);
      setReservationData(formatReservations(response.data));
    } catch (_) {
      // swallow
    }
  }, [currentResourceGroup]); // Re-create when resource group changes

  useEffect(() => {
    // initial fetch as fallback while WS connects
    fetchRooms();
    fetchReservationsHttp(selectedDate);

    // subscribe to WS stream
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
    unsubRef.current = reservationsWS.addListener((list, meta) => {
      // Only accept data for our current selected date (avoid stale updates)
      if (!meta || meta.date === selectedDateRef.current) {
        setReservationData(formatReservations(list));
      }
    });
    reservationsWS.subscribe(selectedDate);

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      // Do not close the WS singleton here; other pages may use it
    };
  }, [fetchRooms, fetchReservationsHttp, selectedDate, currentResourceGroup]);

  useEffect(() => {
    // update subscription when date changes
    reservationsWS.updateSubscription(selectedDate);
    // also fetch via REST once as fast fallback (in case WS hasn't delivered yet)
    fetchReservationsHttp(selectedDate);
  }, [selectedDate, fetchReservationsHttp]);

  const handleDateChange = useCallback((date) => {
    setSelectedDate(date);
  }, []);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTimeSlot(null);
  };

  const triggerImmediateRefresh = () => {
    reservationsWS.refresh();
    fetchReservationsHttp(selectedDate);
  };

  const handleReservationCreated = (created) => {
    triggerImmediateRefresh();
    if (created) {
      const title = created.title || created.description || "Reserved";
      const start = dayjs(created.occurrence_start || created.start).format("YYYY-MM-DD HH:mm");
      const end = dayjs(created.occurrence_end || created.end).format("YYYY-MM-DD HH:mm");
      setSuccessMsg({ title, start, end });
      setTimeout(() => setSuccessMsg(null), 3000);
    }
  };

  return (
    <div className="home-container" dir="ltr">
      <div className="calendar-section">
        <Calendar
          onDateChange={handleDateChange}
          initialSelectedDate={selectedDate}
        />
      </div>
      <div className="timeline-section">
        {rooms.some(room => room.can_create_reservation) && (
          <button
            onClick={() => {
              setSelectedTimeSlot(null);
              handleOpenModal();
            }}
            className="create-event-button"
          >
            {t('create_event')}
          </button>
        )}
        <div className="timeline-wrapper">
          <Timeline
            selectedDate={selectedDate}
            reservationData={reservationData}
            rooms={rooms}
            onTimeSelect={(selection) => {
              setSelectedTimeSlot(selection);
              handleOpenModal();
            }}
            isModalOpen={isModalOpen}
            onReservationDelete={triggerImmediateRefresh}
            onEditReservation={(reservation) => {
              setReservationToEdit(reservation);
              setEditModalOpen(true);
            }}
          />
        </div>
      </div>
      {isModalOpen && (
        <Modal
          onClose={handleCloseModal}
          onEventCreated={handleReservationCreated}
          initialDate={selectedDate}
          initialTimeSlot={selectedTimeSlot}
          rooms={rooms}
        />
      )}
      {editModalOpen && reservationToEdit && (
        <Modal
          onClose={() => {
            setEditModalOpen(false);
            setReservationToEdit(null);
          }}
          onEventEdited={(updated) => {
            triggerImmediateRefresh();
            if (updated) {
              const title = updated.title || updated.description || "Reserved";
              const start = dayjs(updated.occurrence_start || updated.start).format("YYYY-MM-DD HH:mm");
              const end = dayjs(updated.occurrence_end || updated.end).format("YYYY-MM-DD HH:mm");
              setSuccessMsg({ title, start, end });
              setTimeout(() => setSuccessMsg(null), 3000);
            }
          }}
          rooms={rooms}
          reservation={reservationToEdit}
        />
      )}

        {successMsg && (
        <div className="card" style={{ position: 'fixed', bottom: 20, right: 20, background: 'var(--color-success)', color: '#fff' }}>
          <div style={{ fontWeight: 'bold' }}>{t('reservation_success')}</div>
          <div style={{ fontSize: '12px' }}>{successMsg.title}</div>
          <div style={{ fontSize: '12px' }}>{successMsg.start} - {successMsg.end}</div>
        </div>
      )}
    </div>
  );
};

export default Home;
